'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from 'react';
import { type HotTakesSlideDirection, resolveHotTakesSlideDirection } from '@/components/markets/breaking-views/hot-takes-slide-motion';
import type { HeroMarket } from '@/components/markets/featured-market-hero/hero-market';
import { onEvent } from '@/events';
import type { FeaturedHeroCarouselTrigger } from '@/events/featured-hero';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Maximum number of slides surfaced by the carousel. */
const MAX_SLIDES = 10;

/** Auto-advance interval — 12 seconds between slides. */
export const AUTO_ADVANCE_MS = 12_000;

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface UseFeaturedMarketCarouselArgs {
  /** Source list, already ranked by 24h volume. The hook clamps to {@link MAX_SLIDES}. */
  candidateMarkets: ReadonlyArray<HeroMarket>;
  /** When true, auto-advance is disabled (manual navigation still works). */
  prefersReducedMotion?: boolean;
}
export type FeaturedHeroSlidePace = 'auto' | 'manual';
export interface UseFeaturedMarketCarouselReturn {
  /** Top-{@link MAX_SLIDES} slides after filtering. */
  slides: ReadonlyArray<HeroMarket>;
  currentIndex: number;
  /** Slide direction for enter/exit motion — matches Hot Takes carousel. */
  direction: HotTakesSlideDirection;
  /** Manual nav uses a snappier slide; auto-advance keeps the slower timing. */
  slidePace: FeaturedHeroSlidePace;
  /** Currently displayed market, or `null` when there are no slides. */
  currentMarket: HeroMarket | null;
  next: (trigger?: FeaturedHeroCarouselTrigger) => void;
  prev: (trigger?: FeaturedHeroCarouselTrigger) => void;
  goTo: (i: number, trigger?: FeaturedHeroCarouselTrigger) => void;
  /** Increment pause counter (compose hover + focus pauses). */
  pause: () => void;
  /** Decrement pause counter; auto-advance resumes when the counter reaches zero. */
  resume: () => void;
  /** True when at least one source is currently pausing the carousel. */
  isPaused: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Clamp the candidate list down to renderable slides.
 *
 * The upstream query already filters to `status: 'trading'` and ranks by 24h
 * volume, so the only defensive drop here is markets that cannot render a chart grid at all
 * (`outcomes.length < 2`). The clamp then just takes the leading
 * {@link MAX_SLIDES}.
 */
function selectSlides(candidates: ReadonlyArray<HeroMarket>): ReadonlyArray<HeroMarket> {
  const renderable = candidates.filter(m => m.outcomes.length >= 2);
  return renderable.slice(0, MAX_SLIDES);
}

/**
 * Clamp a raw slide index into the renderable range.
 *
 * When the slide list shrinks (e.g. data refetches with fewer markets), the
 * stored index can point past the end. Derive the in-range index during render
 * rather than mirroring a corrected value back into state via an effect.
 */
function clampIndex(rawIndex: number, slideCount: number): number {
  if (slideCount <= 0) return 0;
  return rawIndex >= slideCount ? 0 : rawIndex;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * State management for the Featured Market Hero carousel.
 *
 * Cycles through the top markets by 24h volume with a 12s auto-advance,
 * suspends auto-advance while paused (hover/focus or reduced motion),
 * and emits `featured_hero_carousel_advanced` on every slide change so
 * trigger attribution is preserved across auto / dot / pill / keyboard.
 */
export function useFeaturedMarketCarousel(args) {
  const $ = _c(27);
  const {
    candidateMarkets,
    prefersReducedMotion: t0
  } = args;
  const prefersReducedMotion = t0 === undefined ? false : t0;
  let t1;
  if ($[0] !== candidateMarkets) {
    t1 = selectSlides(candidateMarkets);
    $[0] = candidateMarkets;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const slides = t1;
  const slideCount = slides.length;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [slidePace, setSlidePace] = useState("auto");
  const [pauseCount, setPauseCount] = useState(0);
  let t2;
  if ($[2] !== currentIndex || $[3] !== slideCount) {
    t2 = clampIndex(currentIndex, slideCount);
    $[2] = currentIndex;
    $[3] = slideCount;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const effectiveIndex = t2;
  const indexRef = useRef(effectiveIndex);
  const slideCountRef = useRef(slideCount);
  let t3;
  let t4;
  if ($[5] !== effectiveIndex || $[6] !== slideCount) {
    t3 = () => {
      indexRef.current = effectiveIndex;
      slideCountRef.current = slideCount;
    };
    t4 = [effectiveIndex, slideCount];
    $[5] = effectiveIndex;
    $[6] = slideCount;
    $[7] = t3;
    $[8] = t4;
  } else {
    t3 = $[7];
    t4 = $[8];
  }
  useEffect(t3, t4);
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = function advanceTo(rawNext, trigger, slideDirection) {
      const count = slideCountRef.current;
      if (count <= 0) {
        return;
      }
      const wrapped = (rawNext % count + count) % count;
      const fromSlot = indexRef.current;
      if (wrapped === fromSlot) {
        return;
      }
      setSlidePace(trigger === "auto" ? "auto" : "manual");
      setDirection(slideDirection ?? resolveHotTakesSlideDirection(fromSlot, wrapped));
      setCurrentIndex(wrapped);
      onEvent("featured_hero_carousel_advanced", {
        trigger,
        fromSlot,
        toSlot: wrapped
      });
    };
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const advanceTo = t5;
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = function next(t7) {
      const trigger_0 = t7 === undefined ? "auto" : t7;
      advanceTo(indexRef.current + 1, trigger_0, 1);
    };
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const next = t6;
  let t7;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = function prev(t8) {
      const trigger_1 = t8 === undefined ? "auto" : t8;
      advanceTo(indexRef.current - 1, trigger_1, -1);
    };
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const prev = t7;
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = function goTo(i, t9) {
      const trigger_2 = t9 === undefined ? "auto" : t9;
      advanceTo(i, trigger_2);
    };
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const goTo = t8;
  let t9;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = function pause() {
      setPauseCount(_temp);
    };
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const pause = t9;
  let t10;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = function resume() {
      setPauseCount(_temp2);
    };
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  const resume = t10;
  const isPaused = pauseCount > 0;
  let t11;
  let t12;
  if ($[15] !== isPaused || $[16] !== prefersReducedMotion || $[17] !== slideCount) {
    t11 = () => {
      if (isPaused) {
        return;
      }
      if (prefersReducedMotion) {
        return;
      }
      if (slideCount <= 1) {
        return;
      }
      const handle = setInterval(() => {
        const count_0 = slideCountRef.current;
        if (count_0 <= 0) {
          return;
        }
        const fromSlot_0 = indexRef.current;
        const wrapped_0 = (fromSlot_0 + 1) % count_0;
        if (wrapped_0 === fromSlot_0) {
          return;
        }
        setSlidePace("auto");
        setDirection(1);
        setCurrentIndex(wrapped_0);
        onEvent("featured_hero_carousel_advanced", {
          trigger: "auto",
          fromSlot: fromSlot_0,
          toSlot: wrapped_0
        });
      }, AUTO_ADVANCE_MS);
      return () => {
        clearInterval(handle);
      };
    };
    t12 = [isPaused, slideCount, prefersReducedMotion];
    $[15] = isPaused;
    $[16] = prefersReducedMotion;
    $[17] = slideCount;
    $[18] = t11;
    $[19] = t12;
  } else {
    t11 = $[18];
    t12 = $[19];
  }
  useEffect(t11, t12);
  const currentMarket = slideCount > 0 ? slides[effectiveIndex] ?? slides[0] ?? null : null;
  let t13;
  if ($[20] !== currentMarket || $[21] !== direction || $[22] !== effectiveIndex || $[23] !== isPaused || $[24] !== slidePace || $[25] !== slides) {
    t13 = {
      slides,
      currentIndex: effectiveIndex,
      direction,
      slidePace,
      currentMarket,
      next,
      prev,
      goTo,
      pause,
      resume,
      isPaused
    };
    $[20] = currentMarket;
    $[21] = direction;
    $[22] = effectiveIndex;
    $[23] = isPaused;
    $[24] = slidePace;
    $[25] = slides;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  return t13;
}
function _temp2(c_0) {
  return c_0 > 0 ? c_0 - 1 : 0;
}
function _temp(c) {
  return c + 1;
}